import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Content, Dialog, DialogContainer, Divider, Flex, Heading, Text } from '@adobe/react-spectrum';

import { closeCardInfo } from '../../../data/redux/slices/cardInfoDialogSlice';
import { DynamoConnector } from '../../../data/DynamoConnector';
import { renderManaText } from '../cardStats/manaText';
import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';
import { formatInt, formatNumber } from '../cardStats/formatters';
import { prettySaltCategory } from '../../../utils/SaltCategoryLabels';
import { AffiliateLink } from '../../../affiliates/components/AffiliateLink';
import '../../filterpanel/mana-cost.css';
import './cardInfoDialog.css';

const EFFECT_TYPE_META = {
    trigger: { label: 'Triggered ability', tone: 'red' },
    ability: { label: 'Activated ability', tone: 'gold' },
    static: { label: 'Static ability', tone: 'blue' },
    replacement: { label: 'Replacement effect', tone: 'green' },
    castingCost: { label: 'Casting cost', tone: 'gray' },
};

function prettyActionList(actions) {
    if (!Array.isArray(actions) || actions.length === 0) return null;
    return actions.map((a) => (a || '').toString().replace(/_/g, ' ')).join(', ');
}

function resolveFaceId(cardData, preferredFaceId) {
    if (!cardData) return null;
    const faces = cardData.faces || {};
    if (preferredFaceId && faces[preferredFaceId]) return preferredFaceId;
    if (cardData.frontFaceId && faces[cardData.frontFaceId]) return cardData.frontFaceId;
    return Object.keys(faces)[0] || null;
}

function buildEffectSummary(condition) {
    if (!condition) return [];
    const tags = [];
    const playerThis = condition.player?.this;
    if (playerThis?.type && playerThis.type !== 'all') {
        tags.push({
            label: 'Affects',
            value: playerThis.type === 'self' ? 'you' : playerThis.type === 'opponent' ? 'opponents' : playerThis.type,
        });
    }
    const playerActions = prettyActionList(playerThis?.actions);
    if (playerActions) {
        tags.push({ label: 'Player does', value: playerActions });
    }
    const cardActions = prettyActionList(condition.card?.this?.actions);
    if (cardActions) {
        tags.push({ label: 'Card does', value: cardActions });
    }
    const fromZones = condition.zones?.from || [];
    const toZones = condition.zones?.to || [];
    const fromMeaningful = fromZones.filter((z) => z && z !== 'anywhere');
    const toMeaningful = toZones.filter((z) => z && z !== 'anywhere');
    if (fromMeaningful.length) {
        tags.push({ label: 'From', value: fromMeaningful.join(', ') });
    }
    if (toMeaningful.length) {
        tags.push({ label: 'To', value: toMeaningful.join(', ') });
    }
    const qualKeys = Object.keys(condition.qualifiers || {});
    if (qualKeys.length) {
        const summary = qualKeys
            .map((k) => {
                const q = condition.qualifiers[k];
                if (Array.isArray(q?.list)) return `${k}: ${q.list.join(', ')}`;
                return k;
            })
            .join(' · ');
        tags.push({ label: 'Qualifiers', value: summary });
    }
    if (condition.isEnterOrLeaveEffect) tags.push({ label: 'Flag', value: 'ETB/LTB' });
    if (condition.isOptionalPlayerChoice) tags.push({ label: 'Flag', value: 'Optional (you choose)' });
    if (condition.isSingleActivationOnly) tags.push({ label: 'Flag', value: 'Once per source' });
    if (condition.isSingleTriggerOnly) tags.push({ label: 'Flag', value: 'Once per turn' });
    return tags;
}

export function CardInfoDialog() {
    const dispatch = useDispatch();
    const openCardId = useSelector((s) => s?.cardInfoDialog?.openCardId);
    const isLoading = useSelector((s) => s?.cardInfoDialog?.isLoading);
    const cardData = useSelector((s) => s?.cardInfoDialog?.cardData);
    const error = useSelector((s) => s?.cardInfoDialog?.error);
    const seed = useSelector((s) => s?.cardInfoDialog?.seed);
    // Prints array from Scryfall (one entry per printing) OR null while
    // the request is in flight / failed. An empty array means the search
    // resolved with no hits.
    const [scryfallPrints, setScryfallPrints] = useState(null);
    const [printsLoading, setPrintsLoading] = useState(false);
    const [selectedFaceId, setSelectedFaceId] = useState(null);

    // Reset/realign the selected face whenever the dialog's card changes.
    // `preferredFaceId` is stashed in the seed when the user clicked a
    // specific face (e.g. the back of a MDFC) — we want the dialog to
    // land on that face, not the default front.
    useEffect(() => {
        if (!cardData) {
            setSelectedFaceId(null);
            return;
        }
        setSelectedFaceId(resolveFaceId(cardData, seed?.preferredFaceId));
    }, [cardData, seed?.preferredFaceId]);

    const faces = cardData?.faces || {};
    const faceIds = Object.keys(faces);
    const activeFaceId = selectedFaceId && faces[selectedFaceId] ? selectedFaceId : resolveFaceId(cardData);
    const front = activeFaceId ? faces[activeFaceId] : null;
    const canFlip = faceIds.length > 1;
    const nextFaceName = canFlip
        ? faces[faceIds[(faceIds.indexOf(activeFaceId) + 1) % faceIds.length]]?.name
        : null;
    const flipCard = () => {
        if (!canFlip || !activeFaceId) return;
        const idx = faceIds.indexOf(activeFaceId);
        setSelectedFaceId(faceIds[(idx + 1) % faceIds.length]);
    };

    const metadata = front?.metadata || {};
    const context = front?.context || {};
    const categories = front?.categories?.stats || {};
    const scoring = front?.scoring || {};

    const displayName = front?.name || cardData?.name || seed?.name || 'Card';
    const displayType = metadata.type_line || seed?.typeLine || null;
    const displayOracle = metadata.oracle || seed?.oracle || '';
    const displayManaCost = metadata.mana_cost || seed?.manaCost || '';
    const displayImage =
        metadata.image_uris?.border_crop ||
        metadata.image_uris?.normal ||
        metadata.image_uris?.large ||
        seed?.imageUri ||
        null;
    const keywords = metadata.keywords || [];
    // `salt` at the face/card level is the EDHREC-only score (an unbounded
    // number, not a 0-1 ratio). The meaningful "total" salt comes from
    // `saltCategories.total` which sums every contributing source
    // (edhrec + mld + wipes + taxes + cedhMetashare + etc.). Prefer that
    // for the vitals tile; fall back to the face-level salt if the
    // categories map is missing.
    const saltCategories = front?.saltCategories || cardData?.saltCategories || null;
    const saltTotal = Number(
        saltCategories?.total ?? cardData?.salt ?? front?.salt ?? 0
    );
    const fallbackPrice = cardData?.price?.usd;
    // Cheapest USD across every printing — the "market floor" for grabbing
    // a copy. Falls back to the backend's cached price while Scryfall is
    // in flight. Excludes etched/foil; they render separately per print.
    // We hold onto the winning print itself so the price tile can link
    // directly to that printing on TCGPlayer rather than a generic search.
    const cheapestPrintInfo = (() => {
        if (!Array.isArray(scryfallPrints) || scryfallPrints.length === 0) return null;
        let best = null;
        for (const p of scryfallPrints) {
            const usd = Number(p?.prices?.usd);
            if (!Number.isFinite(usd) || usd <= 0) continue;
            if (best == null || usd < best.usd) best = { usd, print: p };
        }
        return best;
    })();
    const cheapestLiveUsd = cheapestPrintInfo?.usd ?? null;
    const cheapestPrint = cheapestPrintInfo?.print ?? null;
    const displayPrice = cheapestLiveUsd != null ? cheapestLiveUsd.toFixed(2) : fallbackPrice;
    const priceIsLive = cheapestLiveUsd != null;
    const setName = cardData?.set_name;
    const setCode = cardData?.set;
    const isCommanderLegal = cardData?.isCommanderLegal;
    const reservedList = cardData?.reserved === true;
    const artist = metadata.artist;

    // Scryfall pricing is refreshed daily; our own `price` field can lag,
    // so we fire a live prints lookup once we know the card's name (either
    // from the seed or the full details response). We pull ALL printings
    // at once so the dialog can list them with per-print USD/foil/etched
    // prices rather than just showing a single cached value.
    useEffect(() => {
        if (!openCardId) {
            setScryfallPrints(null);
            setPrintsLoading(false);
            return;
        }
        // Use the container-level name (the combined MDFC name, e.g.
        // "Birgi, God of Storytelling // Harnfel, Horn of Bounty") so
        // flipping faces doesn't retrigger the lookup.
        const lookupName = cardData?.name || seed?.name;
        if (!lookupName) return;
        let cancelled = false;
        setPrintsLoading(true);
        DynamoConnector.getScryfallPrints(lookupName, (prints) => {
            if (cancelled) return;
            setScryfallPrints(Array.isArray(prints) ? prints : []);
            setPrintsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [openCardId, cardData?.name, seed?.name]);

    if (!openCardId) return null;

    return (
        <DialogContainer isDismissable onDismiss={() => dispatch(closeCardInfo())}>
            <Dialog
                height="calc(100vh - 100px)"
                UNSAFE_className="cardInfoDialog"
            >
                <Heading>
                    <span className="cardInfoEyebrow">Card info</span>
                    <span className="cardInfoTitle">{displayName}</span>
                    {displayType && <span className="cardInfoTypeLine">{displayType}</span>}
                </Heading>
                <Divider />
                <Content>
                    {error ? (
                        <div className="cardInfoError">
                            <Text>{error}</Text>
                        </div>
                    ) : (
                        <div className="cardInfoBody">
                            <div className="cardInfoImageColumn">
                                <CardImage
                                    uri={displayImage}
                                    name={displayName}
                                    canFlip={canFlip}
                                    onFlip={flipCard}
                                    flipHint={nextFaceName}
                                />
                                <CardVitals
                                    keywords={keywords}
                                    saltTotal={saltTotal}
                                    saltCategories={saltCategories}
                                    price={displayPrice}
                                    priceIsLive={priceIsLive}
                                    pricesLoading={printsLoading}
                                    prints={scryfallPrints}
                                    cheapestPrint={cheapestPrint}
                                    setName={setName}
                                    setCode={setCode}
                                    isCommanderLegal={isCommanderLegal}
                                    reservedList={reservedList}
                                    artist={artist}
                                    cmc={metadata.cmc}
                                    power={metadata.power}
                                    toughness={metadata.toughness}
                                    loyalty={metadata.loyalty}
                                />
                            </div>
                            <div className="cardInfoDataColumn">
                                {isLoading && !displayOracle ? (
                                    <CardInfoSkeleton />
                                ) : (
                                    <>
                                        {displayOracle && <OracleBlock oracle={displayOracle} />}
                                        {Object.keys(categories).length > 0 && (
                                            <CategoriesBlock categories={categories} />
                                        )}
                                        <EffectBreakdownBlock context={context} />
                                        <ScoringBlock scoring={scoring} context={context} />
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </Content>
            </Dialog>
        </DialogContainer>
    );
}

function CardImage({ uri, name, canFlip, onFlip, flipHint }) {
    const [loaded, setLoaded] = useState(false);
    const [errored, setErrored] = useState(false);
    // Re-prime the loader whenever the image source changes so a MDFC
    // face flip re-runs the fade-in and skeleton guard.
    useEffect(() => {
        setLoaded(false);
        setErrored(false);
    }, [uri]);
    const showSkeleton = !uri || (!loaded && !errored);
    return (
        <div className="cardInfoImageFrame">
            {showSkeleton && <div className="cardInfoImageSkeleton" aria-hidden="true" />}
            {uri && !errored && (
                <img
                    src={uri}
                    alt={name || 'Card'}
                    className={`cardInfoImage${loaded ? ' is-loaded' : ''}`}
                    onLoad={() => setLoaded(true)}
                    onError={() => setErrored(true)}
                />
            )}
            {errored && (
                <div className="cardInfoImageFallback">
                    <span className="cardInfoImageFallbackLabel">Image unavailable</span>
                    <span className="cardInfoImageFallbackName">{name}</span>
                </div>
            )}
            {canFlip && (
                <button
                    type="button"
                    className="cardInfoFlipBtn"
                    onClick={onFlip}
                    aria-label={flipHint ? `Flip to ${flipHint}` : 'Flip card'}
                    title={flipHint ? `Flip to ${flipHint}` : 'Flip card'}
                >
                    <span className="cardInfoFlipBtnIcon" aria-hidden="true">↻</span>
                    <span className="cardInfoFlipBtnLabel">Flip</span>
                </button>
            )}
        </div>
    );
}

function CardVitals({
    keywords,
    saltTotal,
    saltCategories,
    price,
    priceIsLive,
    pricesLoading,
    prints,
    cheapestPrint,
    setName,
    setCode,
    isCommanderLegal,
    reservedList,
    artist,
    cmc,
    power,
    toughness,
    loyalty,
}) {
    const hasPT = power != null && toughness != null;
    const priceDisplay = price ? `$${Number(price).toFixed(2)}` : null;
    const safeKeywords = Array.isArray(keywords) ? keywords.filter(Boolean) : [];
    // Extract per-category salt entries sorted desc by score. Skip the
    // `total` key — it's a summary, not a category.
    const saltEntries = saltCategories
        ? Object.entries(saltCategories)
              .filter(([key, val]) => key !== 'total' && Number(val?.score) > 0)
              .map(([key, val]) => ({ key, score: Number(val.score) }))
              .sort((a, b) => b.score - a.score)
        : [];
    const hasSalt = saltEntries.length > 0 || saltTotal > 0;
    const saltTier =
        saltTotal > 8 ? 'alert' : saltTotal > 3 ? 'watch' : saltTotal > 0 ? 'ok' : 'neutral';
    const saltTierLabel =
        saltTier === 'alert' ? 'Extra salty' : saltTier === 'watch' ? 'Salty' : saltTier === 'ok' ? 'Mildly salty' : 'Not salty';
    const hasStatsBlock = cmc != null || hasPT || loyalty != null || safeKeywords.length > 0;
    const hasFlagsBlock = isCommanderLegal === false || reservedList;
    return (
        <div className="cardInfoVitals">
            {hasStatsBlock && (
                <section className="cardInfoVitalsBlock cardInfoVitalsBlock--stats">
                    <span className="cardInfoVitalsBlockLabel">Card stats</span>
                    <div className="cardInfoVitalsGrid">
                        {cmc != null && <VitalTile label="CMC" value={cmc} />}
                        {hasPT && <VitalTile label="P/T" value={`${power}/${toughness}`} />}
                        {loyalty != null && <VitalTile label="Loyalty" value={loyalty} />}
                    </div>
                    {safeKeywords.length > 0 && (
                        <div className="cardInfoKeywordRow">
                            {safeKeywords.map((kw) => (
                                <span key={kw} className="cardInfoKeywordChip">
                                    {kw}
                                </span>
                            ))}
                        </div>
                    )}
                </section>
            )}
            {hasSalt && (
                <section
                    className={`cardInfoVitalsBlock cardInfoVitalsBlock--salt cardInfoVitalsBlock--salt-${saltTier}`}
                >
                    <span className="cardInfoVitalsBlockLabel">Saltiness</span>
                    <div className="cardInfoSaltRow">
                        <div className="cardInfoSaltHero">
                            <span className="cardInfoSaltHeroValue">
                                {saltTotal > 0 ? saltTotal.toFixed(1) : '—'}
                            </span>
                            <span className="cardInfoSaltHeroTier">{saltTierLabel}</span>
                        </div>
                        {saltEntries.length > 0 ? (
                            <div className="cardInfoSaltChips">
                                {saltEntries.map((e) => (
                                    <span
                                        key={e.key}
                                        className="cardInfoSaltChip"
                                        title={`${prettySaltCategory(e.key)} contributes ${e.score.toFixed(2)} to this card\u2019s salt.`}
                                    >
                                        <span className="cardInfoSaltChipLabel">
                                            {prettySaltCategory(e.key)}
                                        </span>
                                        <span className="cardInfoSaltChipScore">
                                            {e.score.toFixed(1)}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <span className="cardInfoSaltEmpty">
                                No categorised salt signal for this card.
                            </span>
                        )}
                    </div>
                </section>
            )}
            <PrintingBlock
                priceDisplay={priceDisplay}
                priceIsLive={priceIsLive}
                pricesLoading={pricesLoading}
                prints={prints}
                cheapestPrint={cheapestPrint}
                setCode={setCode}
                setName={setName}
            />

            {hasFlagsBlock && (
                <div className="cardInfoFlagRow">
                    {isCommanderLegal === false && (
                        <span className="cardInfoFlag cardInfoFlag--alert">Not legal in Commander</span>
                    )}
                    {reservedList && <span className="cardInfoFlag cardInfoFlag--alert">Reserved list</span>}
                </div>
            )}
            {artist && <span className="cardInfoArtist">illustrated by {artist}</span>}
        </div>
    );
}

// A rarity character for the Scryfall print list — compact enough to
// fit in the print row without adding a full text label.
const RARITY_GLYPH = { common: 'C', uncommon: 'U', rare: 'R', mythic: 'M', special: 'S', bonus: 'B' };

function formatPrintDate(iso) {
    if (!iso) return '';
    // Scryfall ships ISO-YYYY-MM-DD — avoid `new Date(iso).toLocaleDateString`
    // which can introduce locale drift and shift dates across time zones.
    const [y, m] = iso.split('-');
    if (!y || !m) return iso;
    return `${y}/${m}`;
}

function formatUsd(n) {
    if (n == null || n === '') return null;
    const num = Number(n);
    if (!Number.isFinite(num)) return null;
    return `$${num.toFixed(2)}`;
}

function PrintingBlock({ priceDisplay, priceIsLive, pricesLoading, prints, cheapestPrint, setCode, setName }) {
    const hasTile = priceDisplay || setCode;
    const printsList = Array.isArray(prints) ? prints : [];
    const showSkeleton = pricesLoading && printsList.length === 0;
    const showList = printsList.length > 0;
    const empty = !pricesLoading && !showList;
    if (!hasTile && !showSkeleton && !showList && !empty) return null;
    // Build the affiliate target for the price tile when we have a live
    // cheapest print: prefer the direct TCGPlayer product page, fall
    // back to a search-by-name when Scryfall lacks a tcgplayer URL for
    // that specific printing.
    const cheapestTarget = (() => {
        if (!priceIsLive || !cheapestPrint) return null;
        if (cheapestPrint.purchaseUris?.tcgplayer) {
            return { kind: 'card', card: { purchaseUris: cheapestPrint.purchaseUris } };
        }
        if (cheapestPrint.name) {
            return { kind: 'search', query: cheapestPrint.name };
        }
        return null;
    })();
    const priceTile = priceDisplay ? (
        <VitalTile
            label={priceIsLive ? 'Cheapest · live' : 'Price'}
            value={priceDisplay}
            accent={priceIsLive ? 'ok' : null}
            hint={priceIsLive ? 'Lowest USD price across every printing shown below.' : 'Cached USD price'}
        />
    ) : null;
    return (
        <section className="cardInfoVitalsBlock cardInfoVitalsBlock--printing">
            <div className="cardInfoVitalsBlockHeader">
                <span className="cardInfoVitalsBlockLabel">Printings</span>
                {showList && (
                    <span className="cardInfoPrintingsCount">
                        {printsList.length} print{printsList.length === 1 ? '' : 's'}
                    </span>
                )}
            </div>
            {hasTile && (
                <div className="cardInfoVitalsGrid">
                    {priceTile &&
                        (cheapestTarget ? (
                            <AffiliateLink
                                provider="tcgplayer"
                                target={cheapestTarget}
                                placement="cheapest-tile"
                                className="cardInfoVitalTileLink"
                                title={`Buy the cheapest printing (${
                                    cheapestPrint?.set?.toUpperCase() || ''
                                }${cheapestPrint?.collectorNumber ? ` #${cheapestPrint.collectorNumber}` : ''}) on TCGPlayer for $${priceDisplay}`}
                                aria-label={`Buy cheapest printing on TCGPlayer for $${priceDisplay}`}
                            >
                                {priceTile}
                            </AffiliateLink>
                        ) : (
                            priceTile
                        ))}
                    {setCode && <VitalTile label="Set" value={setCode.toUpperCase()} hint={setName} />}
                </div>
            )}
            {showSkeleton && <PrintsListSkeleton />}
            {showList && <PrintsList prints={printsList} />}
            {empty && <span className="cardInfoEmpty">Scryfall returned no printings for this card.</span>}
        </section>
    );
}

function PrintsListSkeleton() {
    return (
        <div className="cardInfoPrintsList cardInfoPrintsList--loading" aria-busy="true">
            {[0, 1, 2, 3, 4].map((i) => (
                <div className="cardInfoPrintRow cardInfoPrintRow--skeleton" key={i}>
                    <span className="cardInfoSkelBar cardInfoSkelBar--short" />
                    <span className="cardInfoSkelBar" />
                    <span className="cardInfoSkelBar cardInfoSkelBar--tiny" />
                </div>
            ))}
        </div>
    );
}

function PrintsList({ prints }) {
    return (
        <ol className="cardInfoPrintsList">
            {prints.map((p) => {
                const nonfoil = formatUsd(p.prices?.usd);
                const foil = formatUsd(p.prices?.usd_foil);
                const etched = formatUsd(p.prices?.usd_etched);
                const rarity = (p.rarity || '').toLowerCase();
                const rarityGlyph = RARITY_GLYPH[rarity] || '?';
                // Direct product-page link when Scryfall has a TCGPlayer
                // purchase URL for this printing; otherwise fall back to a
                // TCGPlayer search by card name. Either way the whole row
                // is the affiliate target — buying is the primary action.
                const tcgTarget = p.purchaseUris?.tcgplayer
                    ? { kind: 'card', card: { purchaseUris: p.purchaseUris } }
                    : { kind: 'search', query: p.name };
                return (
                    <li key={p.id} className="cardInfoPrintRow" title={p.setName}>
                        <AffiliateLink
                            provider="tcgplayer"
                            target={tcgTarget}
                            placement="printslist"
                            className="cardInfoPrintLink"
                            title={`Buy ${p.setName || 'this printing'} on TCGPlayer (affiliate link — supports the site)`}
                            aria-label={`Buy ${p.set?.toUpperCase() || ''} #${p.collectorNumber} on TCGPlayer`}
                        >
                            <span className="cardInfoPrintSetCode">{(p.set || '').toUpperCase()}</span>
                            <span className="cardInfoPrintCollector">#{p.collectorNumber}</span>
                            <span
                                className={`cardInfoPrintRarity cardInfoPrintRarity--${rarity}`}
                                title={`${p.rarity || 'unknown'} rarity`}
                            >
                                {rarityGlyph}
                            </span>
                            <span className="cardInfoPrintDate">{formatPrintDate(p.releasedAt)}</span>
                            <span className="cardInfoPrintPrices">
                                {nonfoil ? (
                                    <span className="cardInfoPrintPrice">{nonfoil}</span>
                                ) : (
                                    <span className="cardInfoPrintPrice cardInfoPrintPrice--na">—</span>
                                )}
                                {foil && (
                                    <span className="cardInfoPrintPrice cardInfoPrintPrice--foil" title="Foil">
                                        {foil}
                                    </span>
                                )}
                                {etched && (
                                    <span
                                        className="cardInfoPrintPrice cardInfoPrintPrice--etched"
                                        title="Etched foil"
                                    >
                                        {etched}
                                    </span>
                                )}
                            </span>
                        </AffiliateLink>
                    </li>
                );
            })}
        </ol>
    );
}

function VitalTile({ label, value, hint, accent }) {
    return (
        <div className={`cardInfoVitalTile${accent ? ` cardInfoVitalTile--${accent}` : ''}`} title={hint}>
            <span className="cardInfoVitalTileLabel">{label}</span>
            <span className="cardInfoVitalTileValue">{value}</span>
        </div>
    );
}

function OracleBlock({ oracle }) {
    return (
        <Section title="Rules text" hint="The card's oracle text.">
            <div className="cardInfoOracle">
                {oracle.split('\n').map((line, idx) => (
                    <p className="cardInfoOracleLine" key={idx}>
                        {renderManaText(line)}
                    </p>
                ))}
            </div>
        </Section>
    );
}

function CategoriesBlock({ categories }) {
    const entries = Object.keys(categories || {}).filter((k) => !!categories[k]);
    if (!entries.length) return null;
    return (
        <Section
            title="Roles"
            hint="Commandersalt tags this card with these roles — they drive category scoring downstream."
        >
            <div className="cardInfoRoleChips">
                {entries.map((k) => (
                    <span key={k} className="cardInfoRoleChip">
                        {getPrettyLabel(k)}
                    </span>
                ))}
            </div>
        </Section>
    );
}

function EffectBreakdownBlock({ context }) {
    const lines = Array.isArray(context?.lines) ? context.lines : [];
    if (!lines.length) {
        return (
            <Section title="Parsed effects" hint="How Commandersalt reads each line of this card.">
                <Text UNSAFE_className="cardInfoEmpty">No parsed effects recorded for this card.</Text>
            </Section>
        );
    }
    return (
        <Section
            title="How Commandersalt reads this card"
            hint="Each line of rules text, tagged by effect type with its parsed behavior."
        >
            <div className="cardInfoLineList">
                {lines.map((line, idx) => (
                    <LineCard key={line.id || idx} line={line} />
                ))}
            </div>
        </Section>
    );
}

function LineCard({ line }) {
    const meta = EFFECT_TYPE_META[line.effectType] || {
        label: line.effectType || 'Unknown',
        tone: 'gray',
    };
    return (
        <div className={`cardInfoLineCard cardInfoLineCard--${meta.tone}`}>
            <div className="cardInfoLineHeader">
                <span className={`cardInfoLineBadge cardInfoLineBadge--${meta.tone}`}>{meta.label}</span>
                <span className="cardInfoLineRaw">{renderManaText(line.raw || '')}</span>
            </div>
            <div className="cardInfoLineConditions">
                {(line.parts || []).map((part, pIdx) =>
                    (part.conditions || []).map((cond, cIdx) => (
                        <ConditionCard key={`${part.id || pIdx}::${cond.id || cIdx}`} condition={cond} />
                    ))
                )}
            </div>
        </div>
    );
}

function ConditionCard({ condition }) {
    const summary = buildEffectSummary(condition);
    const results = condition.results || [];
    return (
        <div className="cardInfoCondition">
            <p className="cardInfoConditionText">
                &ldquo;{renderManaText(condition.rawText || condition.text || '')}&rdquo;
            </p>
            {summary.length > 0 && (
                <div className="cardInfoConditionTags">
                    {summary.map((tag, idx) => (
                        <span className="cardInfoConditionTag" key={idx}>
                            <span className="cardInfoConditionTagLabel">{tag.label}</span>
                            <span className="cardInfoConditionTagValue">{tag.value}</span>
                        </span>
                    ))}
                </div>
            )}
            {results.length > 0 && (
                <div className="cardInfoConditionResults">
                    <span className="cardInfoConditionResultsLabel">Results in</span>
                    {results.map((r, idx) => (
                        <div className="cardInfoConditionResult" key={r.id || idx}>
                            <span className="cardInfoConditionResultText">
                                &ldquo;
                                {renderManaText(r.rawText || r.text || '')}
                                &rdquo;
                            </span>
                            {buildEffectSummary(r).length > 0 && (
                                <div className="cardInfoConditionTags cardInfoConditionTags--sub">
                                    {buildEffectSummary(r).map((tag, tIdx) => (
                                        <span className="cardInfoConditionTag" key={`r_${tIdx}`}>
                                            <span className="cardInfoConditionTagLabel">{tag.label}</span>
                                            <span className="cardInfoConditionTagValue">{tag.value}</span>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ScoringBlock({ scoring, context }) {
    const categoryNames = Object.keys(scoring || {});
    if (!categoryNames.length) return null;
    return (
        <Section
            title="Category scores"
            hint="How this card scores within each role. Each entry traces back to the condition / result text that produced the score — cards can have multiple entries per category when several effects qualify."
        >
            <div className="cardInfoScoringList">
                {categoryNames.map((cat) => {
                    const entries = scoring[cat] || [];
                    return (
                        <div className="cardInfoScoringCategory" key={cat}>
                            <div className="cardInfoScoringCategoryHead">
                                <span className="cardInfoScoringCategoryName">{getPrettyLabel(cat)}</span>
                                <span className="cardInfoScoringCategoryCount">
                                    {entries.length} entr
                                    {entries.length === 1 ? 'y' : 'ies'}
                                </span>
                            </div>
                            {entries.map((entry, idx) => (
                                <ScoringEntry
                                    entry={entry}
                                    context={context}
                                    key={`${entry.contextIds?.condition || idx}-${idx}`}
                                />
                            ))}
                        </div>
                    );
                })}
            </div>
        </Section>
    );
}

// Cross-reference a scoring entry's `contextIds` against the card's parsed
// `context.lines[*].parts[*].conditions[*].results[*]` tree so we can show
// the exact (trigger, result) rule-text pair that the backend matched.
// Returns any subset that resolves — the tree is well-formed so matches
// propagate from line → part → condition → result.
function resolveScoringContext(context, contextIds) {
    if (!context || !contextIds) return null;
    const lines = context.lines || {};
    let matchedLine = null;
    let matchedPart = null;
    let matchedPreCondition = null;
    let matchedCondition = null;
    for (const key of Object.keys(lines)) {
        const line = lines[key];
        if (!line) continue;
        if (contextIds.line && line.id !== contextIds.line) continue;
        matchedLine = matchedLine || line;
        for (const part of line.parts || []) {
            if (contextIds.part && part?.id !== contextIds.part) continue;
            matchedPart = matchedPart || part;
            for (const cond of part?.conditions || []) {
                if (contextIds.preCondition && cond?.id === contextIds.preCondition) {
                    matchedPreCondition = cond;
                }
                for (const res of cond?.results || []) {
                    if (contextIds.condition && res?.id === contextIds.condition) {
                        matchedCondition = res;
                        matchedPreCondition = matchedPreCondition || cond;
                    }
                }
            }
        }
    }
    return { line: matchedLine, part: matchedPart, preCondition: matchedPreCondition, condition: matchedCondition };
}

// Which additional-cost keys the backend emits on castability /
// activation. Each maps to a short human label surfaced as a chip when
// the flag is truthy (or when the numeric "qualifiers" count is > 0).
const ADDITIONAL_COST_LABELS = {
    sacrificeSelf: 'Sac self',
    sacrificePermanent: 'Sac permanent',
    payLife: 'Pay life',
    discardCard: 'Discard card',
    discardSelf: 'Discard self',
    exileCard: 'Exile card',
    exileSelf: 'Exile self',
    tapSelf: 'Tap self',
    isDiceRoll: 'Dice roll',
    isSuspend: 'Suspend',
};

// Human labels for the bias components that feed `overallBias`. We render
// every one that's not the neutral 1.0 so the user can see which factor
// actually moved the needle.
const BIAS_COMPONENT_LABELS = {
    castabilityBias: 'Castability',
    preConditionBias: 'Pre-condition',
    activationBias: 'Activation',
    upkeepBias: 'Upkeep',
};

function collectAdditionalCostChips(block, keyPrefix) {
    if (!block?.additionalCosts) return [];
    const out = [];
    for (const [field, label] of Object.entries(ADDITIONAL_COST_LABELS)) {
        if (block.additionalCosts[field]) {
            out.push({ key: `${keyPrefix}-${field}`, label });
        }
    }
    const qualifiers = Number(block.additionalCosts.qualifiers || 0);
    if (qualifiers > 0) {
        out.push({ key: `${keyPrefix}-qualifiers`, label: `+${qualifiers} qualifier${qualifiers === 1 ? '' : 's'}` });
    }
    const loyaltyCost = block.additionalCosts.loyaltyCost;
    if (loyaltyCost != null && loyaltyCost !== 0) {
        out.push({ key: `${keyPrefix}-loyalty`, label: `Loyalty ${loyaltyCost > 0 ? '+' : ''}${loyaltyCost}` });
    }
    const suspendLength = Number(block.additionalCosts.suspendLength || 0);
    if (suspendLength > 0) {
        out.push({ key: `${keyPrefix}-suspend-len`, label: `Suspend ${suspendLength}` });
    }
    const freeCaveat = Number(block.additionalCosts?.freeWithCaveat?.qualifiers || 0);
    if (freeCaveat > 0) {
        out.push({ key: `${keyPrefix}-free-caveat`, label: `Free caveat ×${freeCaveat}` });
    }
    return out;
}

function collectPipBreakdown(block) {
    if (!block) return [];
    const out = [];
    const push = (k, v, label) => {
        if (v > 0) out.push({ key: k, label, value: v });
    };
    push('generic', Number(block.generic || 0), 'Generic');
    push('x', Number(block.x || 0), 'X');
    push('hybrid', Number(block.hybrid || 0), 'Hybrid');
    push('phyrexian', Number(block.phyrexian || 0), 'Phyrexian');
    push('snow', Number(block.snow || 0), 'Snow');
    // symbols is a map keyed by color letter: { u: {count: 1}, r: {count: 2} }
    const symbols = block.symbols || {};
    for (const letter of Object.keys(symbols)) {
        const count = Number(symbols[letter]?.count || 0);
        if (count > 0) {
            out.push({ key: `sym-${letter}`, label: letter.toUpperCase(), value: count });
        }
    }
    return out;
}

// Comprehensive scoring-signal extractor. Returns:
//   - badges: on/off flags rendered as top-of-entry chips
//   - details: label/value pairs rendered as the key-value table
//   - sections: structured sub-blocks (castability, activation, bias
//     breakdown, special) each rendered only when they carry data
// Adding a new backend field = add one entry here; the UI handles layout.
function buildScoringSignals(entry) {
    const badges = [];
    const details = [];
    const sections = [];
    const bias = entry?.bias || {};
    const special = entry?.special || {};
    const castability = entry?.castability || {};
    const activation = entry?.activationCostScoring || {};

    // ---------- Boolean flags → badges ----------
    const flag = (cond, key, label, tone, hint) => {
        if (cond) badges.push({ key, label, tone, hint });
    };
    if (entry?.hasAnySynergyDependencies) {
        badges.push({
            key: 'synergy',
            label: entry.hasComplexSynergyDependencies ? 'Complex synergy' : 'Synergy-dependent',
            tone: 'cool',
            hint: entry.hasComplexSynergyDependencies
                ? 'Score leans on multiple other cards working together.'
                : 'Score depends on at least one other card interacting with this one.',
        });
    }
    flag(entry?.hasGenericKindredDependencies, 'kindred', 'Kindred-leaning', 'cool', 'Score assumes tribal / kindred support in the deck.');
    flag(entry?.hasNoneBaseDependency, 'base-dep', 'Base dependency', 'cool', 'Relies on a baseline effect elsewhere on the card.');
    flag(entry?.isSingleTriggerOnly, 'single-trigger', 'Fires once', 'watch', 'Only one trigger in the card\u2019s lifetime — limits compounding value.');
    flag(entry?.allowSynergyAdjustment === false, 'no-synergy-adj', 'No synergy adjust', 'watch', 'Synergy post-processing is disabled for this entry.');
    flag(special?.isCoinFlip, 'coinflip', 'Coin flip', 'watch', 'Score adjusted for the 50/50 variance this effect introduces.');
    flag(castability?.hasFreeCast, 'free-cast', 'Free cast', 'ok', 'This card can be cast for no mana under some condition.');
    flag(!castability?.hasFreeCast && castability?.hasFreeCastWithCaveat, 'free-cast-caveat', 'Free w/ caveat', 'ok', 'Free to cast with a qualifying condition.');
    flag(castability?.hasReductionEffect, 'cost-reduction', 'Cost reduction', 'ok', 'Reduces the mana cost of this or other spells.');
    flag(castability?.hasSpeedBonus, 'speed-bonus', 'Speed bonus', 'ok', 'Casts faster than its mana value would suggest (e.g. flash, instant-speed).');
    flag(activation?.hasRepeatableActivation, 'repeatable-act', 'Repeatable activation', 'ok', 'Ability can be activated more than once per turn.');
    flag(activation?.hasFreeCast, 'free-activation', 'Free activation', 'ok', 'Can be activated for no mana under some condition.');
    flag(activation?.hasReductionEffect, 'activation-reduction', 'Activation reduction', 'ok', 'Reduces the cost of this or another activation.');
    flag(activation?.hasSpeedBonus, 'activation-speed', 'Activation speed bonus', 'ok', 'Activation is faster than its cost implies.');

    // ---------- Top-level summary details ----------
    const preBias = Number(bias.scorePreBiasAdjustment || 0);
    const overall = Number(bias.overallBias ?? 1);
    if (preBias > 0 || overall !== 1) {
        details.push({
            key: 'bias',
            label: 'Bias',
            value: `${formatNumber(preBias, 1)} \u00d7 ${overall.toFixed(2)} = ${formatNumber(preBias * overall, 1)}`,
            hint: 'Raw category score, multiplied by the overall bias derived from cost, pre-condition, activation, and upkeep factors.',
        });
    }
    if (bias.categoryBias != null && Number(bias.categoryBias) !== 1) {
        details.push({
            key: 'category-bias',
            label: 'Category weight',
            value: `\u00d7${Number(bias.categoryBias).toFixed(2)}`,
            hint: 'Multiplier the role applies before combining with castability / activation biases.',
        });
    }
    if (entry?.postProcessorScore != null && entry.postProcessorScore !== entry.score) {
        details.push({
            key: 'post-processor',
            label: 'Post-processed from',
            value: formatNumber(entry.postProcessorScore, 1),
            hint: 'Base score produced by the category processor before synergy / coin-flip adjustments.',
        });
    }
    if (entry?.scoreRating != null && Number(entry.scoreRating) !== Number(entry.score)) {
        details.push({
            key: 'score-rating',
            label: 'Score rating',
            value: formatNumber(entry.scoreRating, 2),
            hint: 'Alternate score used for ranking / comparison contexts.',
        });
    }

    // ---------- Castability sub-block ----------
    const castabilityDetails = [];
    const castabilityChips = [];
    if (castability.cmc != null && Number(castability.cmc) > 0) {
        castabilityDetails.push({
            key: 'cast-cmc',
            label: 'CMC',
            value: `${castability.cmc}${castability.hasFreeCast ? ' (free)' : ''}`,
        });
    }
    if (
        castability.totalForNonX != null &&
        Number(castability.totalForNonX) !== Number(castability.cmc || 0) &&
        Number(castability.totalForNonX) > 0
    ) {
        castabilityDetails.push({
            key: 'cast-nonx',
            label: 'Non-X cost',
            value: String(castability.totalForNonX),
        });
    }
    if (Number(castability.coloredPipCount || 0) > 0) {
        castabilityDetails.push({
            key: 'cast-colored',
            label: 'Colored pips',
            value: String(castability.coloredPipCount),
        });
    }
    if (castability.score != null && Number(castability.score) !== 0) {
        castabilityDetails.push({
            key: 'cast-score',
            label: 'Castability score',
            value: formatNumber(castability.score, 2),
        });
    }
    if (
        castability.baselineScore != null &&
        Number(castability.baselineScore) !== 0 &&
        Number(castability.baselineScore) !== Number(castability.score || 0)
    ) {
        castabilityDetails.push({
            key: 'cast-baseline',
            label: 'Baseline',
            value: formatNumber(castability.baselineScore, 2),
        });
    }
    const pipChips = collectPipBreakdown(castability);
    const castCostChips = collectAdditionalCostChips(castability, 'cast');
    if (castabilityDetails.length || pipChips.length || castCostChips.length) {
        sections.push({
            key: 'castability',
            title: 'Castability',
            details: castabilityDetails,
            pips: pipChips,
            chips: castCostChips,
        });
    }

    // ---------- Activation sub-block (for activated abilities) ----------
    const activationDetails = [];
    if (activation.cmc != null && Number(activation.cmc) > 0) {
        activationDetails.push({
            key: 'act-cmc',
            label: 'Cost',
            value: `${activation.cmc} mana`,
        });
    }
    if (Number(activation.coloredPipCount || 0) > 0) {
        activationDetails.push({
            key: 'act-colored',
            label: 'Colored pips',
            value: String(activation.coloredPipCount),
        });
    }
    const activationPips = collectPipBreakdown(activation);
    const activationCostChips = collectAdditionalCostChips(activation, 'act');
    if (activationDetails.length || activationPips.length || activationCostChips.length) {
        sections.push({
            key: 'activation',
            title: 'Activation',
            details: activationDetails,
            pips: activationPips,
            chips: activationCostChips,
        });
    }

    // ---------- Bias breakdown (every non-1 component) ----------
    const biasDetails = [];
    for (const [field, label] of Object.entries(BIAS_COMPONENT_LABELS)) {
        const v = bias[field];
        if (v != null && Number(v) !== 1) {
            biasDetails.push({
                key: `bias-${field}`,
                label,
                value: `\u00d7${Number(v).toFixed(2)}`,
            });
        }
    }
    if (
        bias.castabilityBiasGenericCostDeduction != null &&
        Number(bias.castabilityBiasGenericCostDeduction) !== 0
    ) {
        biasDetails.push({
            key: 'bias-generic-deduction',
            label: 'Generic deduction',
            value: `\u2212${Number(bias.castabilityBiasGenericCostDeduction).toFixed(2)}`,
        });
    }
    if (biasDetails.length) {
        sections.push({
            key: 'bias',
            title: 'Bias breakdown',
            details: biasDetails,
        });
    }

    // ---------- Coin-flip / special adjustments ----------
    const specialDetails = [];
    if (special?.preCoinFlipAdjustmentScore != null && special.isCoinFlip) {
        specialDetails.push({
            key: 'precoinflip-score',
            label: 'Pre-coin-flip score',
            value: formatNumber(special.preCoinFlipAdjustmentScore, 2),
        });
    }
    if (special?.preCoinFlipAdjustmentMaxScore != null && special.isCoinFlip) {
        specialDetails.push({
            key: 'precoinflip-max',
            label: 'Pre-coin-flip max',
            value: formatNumber(special.preCoinFlipAdjustmentMaxScore, 2),
        });
    }
    if (entry?.maxScore != null && Number(entry.maxScore) !== Number(entry.score)) {
        specialDetails.push({
            key: 'max-score',
            label: 'Raw max',
            value: formatNumber(entry.maxScore, 2),
            hint: 'Score ceiling before post-processing / synergy adjustments.',
        });
    }
    if (specialDetails.length) {
        sections.push({
            key: 'special',
            title: 'Adjustments',
            details: specialDetails,
        });
    }

    return { badges, details, sections };
}

function ScoringEntry({ entry, context }) {
    const score = Number(entry.score || 0);
    const effectLabel = EFFECT_TYPE_META[entry.effectType]?.label || entry.effectType || '—';
    const { badges, details, sections } = buildScoringSignals(entry);
    const resolved = resolveScoringContext(context, entry.contextIds);
    const triggerText = resolved?.preCondition?.rawText || resolved?.preCondition?.text;
    const resultText =
        resolved?.condition?.rawText || resolved?.condition?.text || entry.conditionText;
    const lineText = resolved?.line?.raw;
    const showTriggerRow = !!triggerText && triggerText !== resultText;
    const zones = resolved?.condition?.zones;
    const playerActions = resolved?.condition?.player?.this?.actions;
    const cardActions = resolved?.condition?.card?.this?.actions;
    // Small sub-chips under the chain: "draw → library → hand", "self",
    // etc. These surface the parsed action schema the backend used to
    // match this entry without dumping the full JSON on the user.
    const semanticChips = [];
    const affects = resolved?.condition?.player?.this?.type;
    if (affects && affects !== 'all') {
        semanticChips.push({ key: 'affects', label: 'Affects', value: affects });
    }
    if (Array.isArray(playerActions) && playerActions.length > 0) {
        semanticChips.push({
            key: 'player-actions',
            label: 'Player',
            value: playerActions.join(', '),
        });
    }
    if (Array.isArray(cardActions) && cardActions.length > 0) {
        semanticChips.push({
            key: 'card-actions',
            label: 'Card',
            value: cardActions.join(', '),
        });
    }
    if (zones && (zones.from?.length || zones.to?.length)) {
        const from = (zones.from || []).filter((z) => z && z !== 'anywhere').join(', ');
        const to = (zones.to || []).filter((z) => z && z !== 'anywhere').join(', ');
        if (from || to) {
            semanticChips.push({
                key: 'zones',
                label: 'Zones',
                value: `${from || 'any'} → ${to || 'any'}`,
            });
        }
    }
    return (
        <div className="cardInfoScoringEntry">
            <div className="cardInfoScoringEntryHeader">
                <span className="cardInfoScoringEffect">{effectLabel}</span>
                <span className="cardInfoScoringScore">{formatNumber(score, 1)}</span>
            </div>
            {(showTriggerRow || resultText) && (
                <div className="cardInfoScoringChain">
                    {showTriggerRow && (
                        <div className="cardInfoScoringChainRow cardInfoScoringChainRow--trigger">
                            <span className="cardInfoScoringChainTag">Fires on</span>
                            <span className="cardInfoScoringChainText">
                                &ldquo;{renderManaText(triggerText)}&rdquo;
                            </span>
                        </div>
                    )}
                    {resultText && (
                        <div className="cardInfoScoringChainRow cardInfoScoringChainRow--result">
                            <span className="cardInfoScoringChainTag">
                                {entry.effectType === 'castingCost'
                                    ? 'Cost'
                                    : entry.effectType === 'static'
                                    ? 'Effect'
                                    : 'Result'}
                            </span>
                            <span className="cardInfoScoringChainText">
                                &ldquo;{renderManaText(resultText)}&rdquo;
                            </span>
                        </div>
                    )}
                </div>
            )}
            {semanticChips.length > 0 && (
                <div className="cardInfoScoringSemanticRow">
                    {semanticChips.map((c) => (
                        <span key={c.key} className="cardInfoScoringSemanticChip">
                            <span className="cardInfoScoringSemanticChipLabel">{c.label}</span>
                            <span className="cardInfoScoringSemanticChipValue">{c.value}</span>
                        </span>
                    ))}
                </div>
            )}
            {badges.length > 0 && (
                <div className="cardInfoScoringBadges">
                    {badges.map((b) => (
                        <span
                            key={b.key}
                            className={`cardInfoScoringBadge cardInfoScoringBadge--${b.tone}`}
                            title={b.hint}
                        >
                            {b.label}
                        </span>
                    ))}
                </div>
            )}
            {details.length > 0 && (
                <dl className="cardInfoScoringDetailList">
                    {details.map((d) => (
                        <div key={d.key} className="cardInfoScoringDetailRow" title={d.hint}>
                            <dt className="cardInfoScoringDetailLabel">{d.label}</dt>
                            <dd className="cardInfoScoringDetailValue">{d.value}</dd>
                        </div>
                    ))}
                </dl>
            )}
            {sections.length > 0 && (
                <div className="cardInfoScoringSections">
                    {sections.map((s) => (
                        <ScoringSubsection key={s.key} section={s} />
                    ))}
                </div>
            )}
            {lineText && lineText !== resultText && (
                <span className="cardInfoScoringSourceLine" title="Full rules-text line the scorer matched against">
                    from: &ldquo;{renderManaText(lineText)}&rdquo;
                </span>
            )}
        </div>
    );
}

function ScoringSubsection({ section }) {
    const hasDetails = (section.details || []).length > 0;
    const hasPips = (section.pips || []).length > 0;
    const hasChips = (section.chips || []).length > 0;
    if (!hasDetails && !hasPips && !hasChips) return null;
    return (
        <div className={`cardInfoScoringSubsection cardInfoScoringSubsection--${section.key}`}>
            <span className="cardInfoScoringSubsectionTitle">{section.title}</span>
            {hasDetails && (
                <dl className="cardInfoScoringDetailList cardInfoScoringDetailList--sub">
                    {section.details.map((d) => (
                        <div key={d.key} className="cardInfoScoringDetailRow" title={d.hint}>
                            <dt className="cardInfoScoringDetailLabel">{d.label}</dt>
                            <dd className="cardInfoScoringDetailValue">{d.value}</dd>
                        </div>
                    ))}
                </dl>
            )}
            {hasPips && (
                <div className="cardInfoScoringPipRow">
                    {section.pips.map((p) => (
                        <span key={p.key} className="cardInfoScoringPipChip" title={`${p.label}: ${p.value}`}>
                            <span className="cardInfoScoringPipLabel">{p.label}</span>
                            <span className="cardInfoScoringPipValue">{p.value}</span>
                        </span>
                    ))}
                </div>
            )}
            {hasChips && (
                <div className="cardInfoScoringCostChips">
                    {section.chips.map((c) => (
                        <span key={c.key} className="cardInfoScoringCostChip">
                            {c.label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function Section({ title, hint, children }) {
    return (
        <section className="cardInfoSection">
            <header className="cardInfoSectionHeader">
                <span className="cardInfoSectionTitle">{title}</span>
                {hint && <span className="cardInfoSectionHint">{hint}</span>}
            </header>
            {children}
        </section>
    );
}

function CardInfoSkeleton() {
    return (
        <Flex direction="column" rowGap={10}>
            <div className="cardInfoSkelRow" />
            <div className="cardInfoSkelRow cardInfoSkelRow--short" />
            <div className="cardInfoSkelRow" />
            <div className="cardInfoSkelRow cardInfoSkelRow--short" />
            <div className="cardInfoSkelBlock" />
            <div className="cardInfoSkelBlock" />
        </Flex>
    );
}
